/**
 * @module MixedAxisConfig/useMixedAxisDraft
 * @description 组合图 Y 轴惰性 draft — 无持久化时仅 UI 展示默认值，用户改动后整组写入 draft
 */

import { useCallback, useMemo } from 'react';
import {
  useChartSettingsDispatch,
  useChartSettingsSelector,
} from '../../context';
import { CSActionType } from '../../types';
import { normalizeMixedAxisFromStorage } from '../common/DualAxis/axisMetricUtils';
import {
  buildDefaultMixedAxisPair,
  hasPersistedMixedAxes,
} from './mixedAxisDefaults';
import type { IMixedAxisConfig, TAxisKey } from '../common/DualAxis/types';

interface IMixedAxisDraftView {
  primaryAxis: IMixedAxisConfig;
  secondaryAxis: IMixedAxisConfig;
}

function selectMixedAxisDraftView(
  columnMetrics: Record<string, unknown>[],
): (state: {
  base: Record<string, unknown>;
  draft: Record<string, unknown>;
}) => IMixedAxisDraftView {
  return (state) => {
    if (!hasPersistedMixedAxes(state.base, state.draft)) {
      const defaults = buildDefaultMixedAxisPair(columnMetrics);
      return {
        primaryAxis: defaults.primaryYAxis,
        secondaryAxis: defaults.secondYAxis,
      };
    }

    return {
      primaryAxis: normalizeMixedAxisFromStorage(
        state.draft.primaryYAxis ?? state.base.primaryYAxis,
        {
          yAxisRangeSwitch:
            state.draft.yAxisRangeSwitch ?? state.base.yAxisRangeSwitch,
        },
      ),
      secondaryAxis: normalizeMixedAxisFromStorage(
        state.draft.secondYAxis ?? state.base.secondYAxis,
        {
          yAxisRangeSwitch:
            state.draft.yAxisRangeSwitch2 ?? state.base.yAxisRangeSwitch2,
        },
      ),
    };
  };
}

export function useMixedAxisDraft(
  columnMetrics: Record<string, unknown>[],
) {
  const dispatch = useChartSettingsDispatch();
  const axisSelector = useMemo(
    () => selectMixedAxisDraftView(columnMetrics),
    [columnMetrics],
  );
  const { primaryAxis, secondaryAxis } = useChartSettingsSelector(axisSelector);

  const commitMixedAxisDraft = useCallback(
    (payload: Partial<Record<TAxisKey, IMixedAxisConfig>>): void => {
      dispatch({
        type: CSActionType.UPDATE_DRAFT,
        payload: {
          primaryYAxis: payload.primaryYAxis
            ? { ...primaryAxis, ...payload.primaryYAxis }
            : primaryAxis,
          secondYAxis: payload.secondYAxis
            ? { ...secondaryAxis, ...payload.secondYAxis }
            : secondaryAxis,
        },
      });
    },
    [dispatch, primaryAxis, secondaryAxis],
  );

  const dispatchAxis = useCallback(
    (axisKey: TAxisKey, patch: Partial<IMixedAxisConfig>): void => {
      const currentAxis =
        axisKey === 'primaryYAxis' ? primaryAxis : secondaryAxis;
      commitMixedAxisDraft({
        [axisKey]: { ...currentAxis, ...patch },
      });
    },
    [commitMixedAxisDraft, primaryAxis, secondaryAxis],
  );

  const dispatchAxes = useCallback(
    (payload: Partial<Record<TAxisKey, IMixedAxisConfig>>): void => {
      commitMixedAxisDraft(payload);
    },
    [commitMixedAxisDraft],
  );

  return {
    primaryAxis,
    secondaryAxis,
    dispatchAxis,
    dispatchAxes,
  };
}
